import { formatCompactMoney } from "../deal-card-facts";
import { toleratedRange } from "./match-where";
import type { BuyBoxCriteria, BuyBoxRange } from "./parse-criteria";

export type BuyBoxFitOutcome = "match" | "near" | "unknown" | "miss";

export interface BuyBoxFitReason {
  key:
    | "industry"
    | "location"
    | "askingPrice"
    | "revenue"
    | "sde"
    | "ebitda"
    | "dealStructure";
  label: string;
  outcome: BuyBoxFitOutcome;
}

export interface BuyBoxFit {
  score: number;
  reasons: BuyBoxFitReason[];
}

/** The listing facts fit is scored on; callers map database rows into this. */
export interface BuyBoxListingFacts {
  businessTypeId: number | null;
  businessTypeName?: string | null;
  countryCode: string | null;
  /** The address subdivision and all of its ancestors. */
  subdivisionIds: number[];
  /** Buy box areas it could be in, when its location is known only roughly. */
  possibleSubdivisionIds?: number[];
  locationLabel?: string | null;
  askingPrice: number | null;
  revenue: number | null;
  sde: number | null;
  ebitda: number | null;
  sbaPrequalified: boolean | null;
  financingAvailable: boolean | null;
}

const WEIGHTS: Record<BuyBoxFitReason["key"], number> = {
  industry: 25,
  location: 20,
  askingPrice: 20,
  sde: 15,
  revenue: 10,
  ebitda: 10,
  dealStructure: 10,
};

const CREDIT: Record<BuyBoxFitOutcome, number> = {
  match: 1,
  near: 0.6,
  unknown: 0.5,
  miss: 0,
};

const FINANCIAL_LABELS = {
  askingPrice: "Asking price",
  revenue: "Revenue",
  sde: "SDE",
  ebitda: "EBITDA",
} as const;

function financialReason(
  key: keyof typeof FINANCIAL_LABELS,
  value: number | null,
  range: BuyBoxRange,
): BuyBoxFitReason | null {
  const bounds = toleratedRange(range);
  if (!bounds) return null;
  const name = FINANCIAL_LABELS[key];
  const money = formatCompactMoney(value);
  if (value == null || !money) {
    return { key, outcome: "unknown", label: `${name} not disclosed` };
  }
  const below = range.min != null && value < range.min;
  const above = range.max != null && value > range.max;
  if (!below && !above) {
    return { key, outcome: "match", label: `${name} ${money}, in your range` };
  }
  const near =
    (bounds.min == null || value >= bounds.min) &&
    (bounds.max == null || value <= bounds.max);
  const side = below ? "below" : "above";
  return near
    ? {
        key,
        outcome: "near",
        label: `${name} ${money}, just ${side} your range`,
      }
    : { key, outcome: "miss", label: `${name} ${money}, ${side} your range` };
}

function industryReason(
  listing: BuyBoxListingFacts,
  criteria: BuyBoxCriteria,
): BuyBoxFitReason | null {
  if (criteria.businessTypeIds.length === 0) return null;
  const name = listing.businessTypeName ?? "This business type";
  if (listing.businessTypeId == null) {
    return {
      key: "industry",
      outcome: "unknown",
      label: "Business type not listed",
    };
  }
  return criteria.businessTypeIds.includes(listing.businessTypeId)
    ? { key: "industry", outcome: "match", label: `${name} is in your buy box` }
    : {
        key: "industry",
        outcome: "miss",
        label: `${name} is outside your buy box`,
      };
}

function locationReason(
  listing: BuyBoxListingFacts,
  criteria: BuyBoxCriteria,
): BuyBoxFitReason | null {
  if (criteria.locations.length === 0) return null;
  if (!listing.countryCode && listing.subdivisionIds.length === 0) {
    return {
      key: "location",
      outcome: "unknown",
      label: "Location not listed",
    };
  }
  const inside = criteria.locations.find((location) =>
    location.subdivisionId == null
      ? location.countryCode === listing.countryCode
      : listing.subdivisionIds.includes(location.subdivisionId),
  );
  if (inside) {
    return { key: "location", outcome: "match", label: `In ${inside.label}` };
  }
  const where = listing.locationLabel ?? "This location";
  const possible = criteria.locations.find(
    (location) =>
      location.subdivisionId != null &&
      listing.possibleSubdivisionIds?.includes(location.subdivisionId),
  );
  if (possible) {
    return {
      key: "location",
      outcome: "unknown",
      label: `${where} could be in ${possible.label}; the exact location isn't stated`,
    };
  }
  return {
    key: "location",
    outcome: "miss",
    label: `${where} is outside your areas`,
  };
}

/** Only SBA and seller financing have listing signals; other structures are not scored. */
function dealStructureReason(
  listing: BuyBoxListingFacts,
  criteria: BuyBoxCriteria,
): BuyBoxFitReason | null {
  const wantsSba = criteria.dealStructures.includes("SBA");
  const wantsSeller = criteria.dealStructures.includes("SELLER_FINANCING");
  if (!wantsSba && !wantsSeller) return null;
  if (wantsSba && listing.sbaPrequalified) {
    return {
      key: "dealStructure",
      outcome: "match",
      label: "SBA prequalified",
    };
  }
  if (wantsSeller && listing.financingAvailable) {
    return {
      key: "dealStructure",
      outcome: "match",
      label: "Seller financing available",
    };
  }
  if (wantsSeller && !wantsSba && listing.financingAvailable === false) {
    return {
      key: "dealStructure",
      outcome: "miss",
      label: "No seller financing offered",
    };
  }
  return {
    key: "dealStructure",
    outcome: "unknown",
    label: "Financing terms not listed",
  };
}

/**
 * How well a listing fits a buy box, from 0 to 100, with one plain-language
 * reason per criterion the buy box sets. Criteria the buy box leaves open
 * don't count either way; undisclosed figures earn half credit.
 */
export function scoreBuyBoxFit(
  listing: BuyBoxListingFacts,
  criteria: BuyBoxCriteria,
): BuyBoxFit {
  const reasons = [
    industryReason(listing, criteria),
    locationReason(listing, criteria),
    financialReason("askingPrice", listing.askingPrice, criteria.askingPrice),
    financialReason("sde", listing.sde, criteria.sde),
    financialReason("revenue", listing.revenue, criteria.revenue),
    financialReason("ebitda", listing.ebitda, criteria.ebitda),
    dealStructureReason(listing, criteria),
  ].filter((reason): reason is BuyBoxFitReason => reason != null);

  if (reasons.length === 0) return { score: 0, reasons };
  const possible = reasons.reduce(
    (sum, reason) => sum + WEIGHTS[reason.key],
    0,
  );
  const earned = reasons.reduce(
    (sum, reason) => sum + WEIGHTS[reason.key] * CREDIT[reason.outcome],
    0,
  );
  return { score: Math.round((earned / possible) * 100), reasons };
}

/** The fit explanation stored on a pursuit created from a match. */
export function formatBuyBoxFitExplanation(fit: BuyBoxFit): string {
  return fit.reasons.map((reason) => `- ${reason.label}`).join("\n");
}
